import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { records } from "../../types";

const recordsSliceState: records.RecordsStateType = {
  getHealthRecords: {
    data: undefined,
    page: "1",
    isLastPageReached: false,
    isLoading: false,
    errors: "",
  },
  updateHealthRecords: {
    isLoading: false,
    errors: "",
    data: null,
  },
  getTestTypes: {
    data: undefined,
    isLoading: false,
    errors: "",
  },
  deleteRecord: {
    isLoading: false,
    errors: "",
    isSuccess: null,
  },
  activeFilters: {
    page: "1",
    sort: "-1",
  },
  searchRecordFilterPage: "1",
  isRecordsExist: false,
};

export const recordsSlice = createSlice({
  name: "records",
  initialState: recordsSliceState,
  reducers: {
    // Get Health records
    healthRecordsPageUpdate: (
      state: records.RecordsStateType,
      action: PayloadAction<any>
    ) => {
      state.getHealthRecords.page = action.payload;
    },
    getHealthRecordsRequest: (
      state: records.RecordsStateType,
      _action: PayloadAction<any>
    ) => {
      state.getHealthRecords.isLoading = true;
      state.getHealthRecords.errors = "";
    },
    getHealthRecordsSuccess: (
      state: records.RecordsStateType,
      action: PayloadAction<any>
    ) => {
      state.getHealthRecords.isLoading = false;
      if ((action.payload?.data ?? []).length > 0) {
        state.isRecordsExist = true;
      }
      state.getHealthRecords.isLastPageReached =
        (action.payload?.data ?? []).length < 5 ? true : false;
      state.getHealthRecords.data =
        state.activeFilters?.page === "1"
          ? action.payload?.data ?? []
          : [
              ...(state.getHealthRecords?.data ?? []),
              ...(action.payload?.data ?? []),
            ];
    },
    getHealthRecordsError: (
      state: records.RecordsStateType,
      { payload: error }: PayloadAction<string>
    ) => {
      state.getHealthRecords.isLoading = false;
      state.getHealthRecords.errors = error;
    },
    searchHealthRecordsRequest: (
      state: records.RecordsStateType,
      _action: PayloadAction<any>
    ) => {
      state.getHealthRecords.isLoading = true;
      state.getHealthRecords.errors = "";
    },
    searchHealthRecordsSuccess: (
      state: records.RecordsStateType,
      action: PayloadAction<any>
    ) => {
      state.getHealthRecords.isLoading = false;
      state.getHealthRecords.isLastPageReached =
        (action.payload?.data ?? []).length < 5 ? true : false;
      state.getHealthRecords.data =
        state.searchRecordFilterPage === "1"
          ? action.payload?.data ?? []
          : [
              ...(state.getHealthRecords?.data ?? []),
              ...(action.payload?.data ?? []),
            ];
    },
    searchHealthRecordsError: (
      state: records.RecordsStateType,
      { payload: error }: PayloadAction<string>
    ) => {
      state.getHealthRecords.isLoading = false;
      state.getHealthRecords.errors = error;
    },

    // Update Health Records
    updateHealthRecordRequest: (
      state: records.RecordsStateType,
      _action: PayloadAction<any>
    ) => {
      state.updateHealthRecords.isLoading = true;
      state.updateHealthRecords.errors = "";
      state.updateHealthRecords.data = null;
    },
    updateHealthRecordSuccess: (
      state: records.RecordsStateType,
      _action: PayloadAction<any>
    ) => {
      state.updateHealthRecords.isLoading = false;
      state.updateHealthRecords.errors = "";
      state.updateHealthRecords.data = _action.payload;
    },
    updateHealthRecordError: (
      state: records.RecordsStateType,
      { payload: error }: PayloadAction<string>
    ) => {
      state.updateHealthRecords.isLoading = false;
      state.updateHealthRecords.errors = error;
    },

    // get test types
    getTestTypesRequest: (
      state: records.RecordsStateType,
      _action: PayloadAction<any>
    ) => {
      state.getTestTypes.isLoading = true;
      state.getTestTypes.errors = "";
    },
    getTestTypesSuccess: (
      state: records.RecordsStateType,
      action: PayloadAction<any>
    ) => {
      state.getTestTypes.isLoading = false;
      state.getTestTypes.data = action.payload?.data;
    },
    getTestTypesError: (
      state: records.RecordsStateType,
      { payload: error }: PayloadAction<string>
    ) => {
      state.getTestTypes.isLoading = false;
      state.getTestTypes.errors = error;
    },

    // get test types
    deleteRecordRequest: (
      state: records.RecordsStateType,
      _action: PayloadAction<any>
    ) => {
      state.deleteRecord.isLoading = true;
      state.deleteRecord.errors = "";
    },
    deleteRecordSuccess: (
      state: records.RecordsStateType,
      action: PayloadAction<any>
    ) => {
      state.deleteRecord.isLoading = false;
      state.deleteRecord.isSuccess = true;
    },
    deleteRecordError: (
      state: records.RecordsStateType,
      { payload: error }: PayloadAction<string>
    ) => {
      state.deleteRecord.isLoading = false;
      state.deleteRecord.errors = error;
      state.deleteRecord.isSuccess = false;
    },

    deleteRecordFromSlice: (
      state: records.RecordsStateType,
      action: PayloadAction<any>
    ) => {
      const recordsAfterDelettion = state.getHealthRecords.data?.filter(
        (i) => i?.patient_records_id !== action.payload
      );
      state.getHealthRecords.data = recordsAfterDelettion;
    },

    // update active filters
    updateActiveFilters: (
      state: records.RecordsStateType,
      action: PayloadAction<any>
    ) => {
      state.activeFilters = action?.payload;
    },
    resetActiveFilters: (
      state: records.RecordsStateType,
      action: PayloadAction<any>
    ) => {
      state.activeFilters = {
        page: "1",
        sort: "-1",
      };
    },
    updateSearchRecordFilter: (
      state: records.RecordsStateType,
      action: PayloadAction<any>
    ) => {
      state.searchRecordFilterPage = action?.payload;
    },
  },
});

export const {
  // get Health records
  healthRecordsPageUpdate,
  getHealthRecordsError,
  getHealthRecordsRequest,
  getHealthRecordsSuccess,

  //Update HealthRecords
  updateHealthRecordError,
  updateHealthRecordRequest,
  updateHealthRecordSuccess,

  // get test types
  getTestTypesError,
  getTestTypesRequest,
  getTestTypesSuccess,

  // delete records
  deleteRecordError,
  deleteRecordRequest,
  deleteRecordSuccess,

  deleteRecordFromSlice,
  updateActiveFilters,
  resetActiveFilters,
  searchHealthRecordsError,
  searchHealthRecordsRequest,
  searchHealthRecordsSuccess,
  updateSearchRecordFilter,
} = recordsSlice.actions;
export default recordsSlice.reducer;
